import { getStoredAuthUser } from "../utils/authSession";

/**
 * Central API origins — all hosts come from env (no hardcoded URLs).
 *
 * Mosida API:
 * - REACT_APP_MOSIDA_API_ORIGIN — host only (no /api)
 * - REACT_APP_BASE_URL_WS — host only
 * - REACT_APP_BASE_URL — may include /api (stripped for origin)
 *
 * AI services:
 * - REACT_APP_AI_SERVICES_ORIGIN
 * - REACT_APP_FAST_API_URL
 * - REACT_APP_BASE_BACKGROUND_REMOVAL_URL
 *
 * Payment / QR redirect:
 * - REACT_APP_PAYMENT_REDIRECT_URL
 * - REACT_APP_FRONTEND_URL
 * - falls back to window.location.origin
 */

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

function readEnvUrl(...keys: string[]): string {
  for (const key of keys) {
    const value = process.env[key]?.trim();
    if (value) return stripTrailingSlash(value);
  }
  return "";
}

function getMosidaApiOrigin(): string {
  const origin = readEnvUrl(
    "REACT_APP_MOSIDA_API_ORIGIN",
    "REACT_APP_BASE_URL_WS",
    "REACT_APP_scanTicketUrl"
  );
  if (origin) return origin;

  const legacyBase = readEnvUrl("REACT_APP_BASE_URL");
  if (legacyBase) {
    return legacyBase.replace(/\/api\/?$/, "") || legacyBase;
  }

  return "";
}

function getMosidaApiBaseUrl(): string {
  const baseWithApi = readEnvUrl("REACT_APP_BASE_URL");
  if (baseWithApi) {
    return /\/api$/i.test(baseWithApi) ? baseWithApi : `${baseWithApi}/api`;
  }

  const origin = getMosidaApiOrigin();
  return origin ? `${origin}/api` : "";
}

function getAiServicesOrigin(): string {
  return readEnvUrl(
    "REACT_APP_AI_SERVICES_ORIGIN",
    "REACT_APP_FAST_API_URL",
    "REACT_APP_BASE_BACKGROUND_REMOVAL_URL"
  );
}

/**
 * Checkout / mobile web host embedded in payment and upload QR codes (no trailing slash).
 */
export function getPaymentRedirectOrigin(): string {
  const fromEnv = readEnvUrl(
    "REACT_APP_PAYMENT_REDIRECT_URL",
    "REACT_APP_FRONTEND_URL"
  );
  if (fromEnv) return fromEnv;

  if (typeof window !== "undefined" && window.location?.origin) {
    return stripTrailingSlash(window.location.origin);
  }

  return "";
}

/** Mobile checkout type derived from cart line types. */
export type PaymentOrderType = "onsite" | "offsite";

export function resolvePaymentOrderType(
  items: Array<{ type?: string }>,
  paymentType?: PaymentOrderType
): PaymentOrderType {
  if (paymentType) return paymentType;
  const allOnsitePrinting =
    items.length > 0 &&
    items.every((item) => item.type === "onsite-printing");
  return allOnsitePrinting ? "onsite" : "offsite";
}

/** Mobile checkout URL after QR scan: kiosk (onsite) vs shipping (offsite). */
export function resolvePaymentRedirectUrl(
  items: Array<{ type?: string }>,
  options?: { paymentType?: PaymentOrderType; userId?: string | null }
): string {
  const origin = getPaymentRedirectOrigin();
  const orderType = resolvePaymentOrderType(items, options?.paymentType);
  const path =
    orderType === "onsite" ? "kiosk-payment-link-onsite" : "shipping-information";

  const params = new URLSearchParams({ type: orderType });
  // Prefer explicit userId; otherwise use the logged-in kiosk session for save-card.
  const userId = options?.userId ?? getStoredAuthUser()?.id ?? null;
  if (userId) {
    params.set("user_id", String(userId));
  }

  const query = params.toString();
  return origin ? `${origin}/${path}?${query}` : `/${path}?${query}`;
}

/** Host only. Used for Socket.IO and URLs that add their own /api segment. */
export const MOSIDA_API_ORIGIN = getMosidaApiOrigin();

/** REST base including /api. */
export const MOSIDA_API_BASE_URL = getMosidaApiBaseUrl();

/** Google AI / shared AI services host (character gen, remove-bg, etc.). */
export const AI_SERVICES_ORIGIN = getAiServicesOrigin();
